import React from 'react';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import moment from 'moment';
import {injectIntl} from 'react-intl';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

import Loader from '../dashboard/Loader';
import ErrorModal from '../ui/ErrorModal';
import API from '../../common/utils/API';
import {colorForKind, labelFor} from './factLabels';

const RANGE_OPTIONS = [
    {value: 7, labelId: 'RELEVANCE.DEMAND_RANGE_7'},
    {value: 30, labelId: 'RELEVANCE.DEMAND_RANGE_30'},
    {value: 90, labelId: 'RELEVANCE.DEMAND_RANGE_90'},
];

export function groupSeriesByDay(series) {
    const byDay = {};
    const kinds = [];

    series.forEach(({day, kind, count}) => {
        if (!kinds.includes(kind)) {
            kinds.push(kind);
        }
        if (!byDay[day]) {
            byDay[day] = {day};
        }
        byDay[day][kind] = count;
    });

    const rows = Object.keys(byDay)
        .sort()
        .map((day) => byDay[day]);

    return {rows, kinds: kinds.sort()};
}

export class DemandChart extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: false,
            range: 30,
            eventId: '',
            series: [],
        };

        this.requestSeq = 0;
        this.fetchData = this.fetchData.bind(this);
        this.handleRangeChange = this.handleRangeChange.bind(this);
        this.handleEventIdChange = this.handleEventIdChange.bind(this);
    }

    async componentDidMount() {
        await this.fetchData();
    }

    async fetchData() {
        const requestId = ++this.requestSeq;

        this.setState({loading: true});

        try {
            const to = moment.utc().format('YYYY-MM-DD');
            const from = moment
                .utc()
                .subtract(this.state.range, 'days')
                .format('YYYY-MM-DD');
            const filters = {from, to};

            if (this.state.eventId) {
                filters.eventId = this.state.eventId;
            }

            const series = await API.reports.getRelevanceDemand(filters);

            if (requestId !== this.requestSeq) {
                return;
            }

            this.setState({series, loading: false, error: false});
        } catch (e) {
            if (requestId !== this.requestSeq) {
                return;
            }

            this.setState({loading: false, error: true});
        }
    }

    handleRangeChange(range) {
        this.setState({range}, this.fetchData);
    }

    handleEventIdChange(event) {
        this.setState({eventId: event.target.value});
    }

    render() {
        const intl = this.props.intl;
        const {series, loading, error, range, eventId} = this.state;
        const {rows, kinds} = groupSeriesByDay(series);

        return (
            <div
                data-test='demandChartComponent'
                style={{position: 'relative'}}
            >
                <Loader loading={loading} />
                <Row className='align-items-center mb-2'>
                    <Col md={6}>
                        <ButtonGroup toggle>
                            {RANGE_OPTIONS.map((option) => (
                                <ToggleButton
                                    key={option.value}
                                    type='radio'
                                    variant='secondary'
                                    name='demand-range'
                                    value={option.value}
                                    checked={range === option.value}
                                    onChange={() =>
                                        this.handleRangeChange(option.value)
                                    }
                                >
                                    {intl.formatMessage({id: option.labelId})}
                                </ToggleButton>
                            ))}
                        </ButtonGroup>
                    </Col>
                    <Col md={4}>
                        <Form.Control
                            type='number'
                            data-test='demandEventIdFilter'
                            placeholder={intl.formatMessage({
                                id: 'RELEVANCE.DEMAND_EVENT_FILTER_PLACEHOLDER',
                            })}
                            value={eventId}
                            onChange={this.handleEventIdChange}
                        />
                    </Col>
                    <Col md={2}>
                        <Button
                            variant='secondary'
                            size='sm'
                            onClick={this.fetchData}
                            data-test='demandRefreshButton'
                        >
                            {intl.formatMessage({
                                id: 'RELEVANCE.REFRESH_BUTTON',
                            })}
                        </Button>
                    </Col>
                </Row>
                {rows.length === 0 ? (
                    <p data-test='demandEmptyState'>
                        {intl.formatMessage({id: 'RELEVANCE.DEMAND_EMPTY'})}
                    </p>
                ) : (
                    <Card className='mb-4'>
                        <Card.Body
                            style={{
                                width: '100%',
                                height: 350,
                                position: 'relative',
                            }}
                        >
                            <ResponsiveContainer>
                                <BarChart data={rows}>
                                    <CartesianGrid strokeDasharray='3 3' />
                                    <XAxis dataKey='day' />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    {kinds.map((kind) => (
                                        <Bar
                                            key={kind}
                                            dataKey={kind}
                                            name={labelFor(
                                                intl,
                                                'FACT_KIND',
                                                kind,
                                            )}
                                            stackId='a'
                                            fill={colorForKind(kind)}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </Card.Body>
                    </Card>
                )}
                <ErrorModal
                    isLoadErrorModalOpen={error}
                    toggleLoadErrorModal={() => this.setState({error: false})}
                    errorMessage={intl.formatMessage({
                        id: 'RELEVANCE.LOAD_ERROR',
                    })}
                    handleRetryClick={this.fetchData}
                    returnUrl='/relevance'
                    loading={loading}
                />
            </div>
        );
    }
}

export default injectIntl(DemandChart);
